import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  CreateSixModalInformation,
  CreateSixState,
  CreateSixStep,
  LocalDraft,
} from '@/types/create-six.types';
import { createSixThunk } from '@/store/thunks/create-six/createSixThunk';
import type { SixSummary , Six } from '@/types/six/Six';
import { validateSixName } from '@/store/thunks/create-six/validateSixThunk';
import { isFieldErrors } from '@/store/utils';
import { fetchCreateSixModalInformation } from '@/store/thunks/create-six/fetchCreateSixModalInformation';
import type { ParticipantsResponseDTO } from '@/types/SixDetailDTOs';

const FIRST_STEP: CreateSixStep = 'Name';

const initialState: CreateSixState = {
  draft: {
    ownerIds: [],
    internalParticipantIds: [],
    externalParticipantIds: [],
    newExternalUsers: [],
    name: '',
    description: '',
  },
  step: FIRST_STEP,
  submitting: false,
  fieldErrors: {},
  internalUsers: [],
  invitedUser: null,
  externalUsers: [],
  loadingInternal: false,
  loadingExternal: false,
  showSuccessScreen: false,
  editingSixId: null,
};

export const createSixSlice = createSlice({
  name: 'createSix',
  initialState,
  reducers: {
    setDraftField<K extends keyof LocalDraft>(
      state: CreateSixState,
      action: PayloadAction<{ key: K; value: LocalDraft[K] }>
    ) {
      const {
        payload: { value },
      } = action;
      state.draft[action.payload.key] = value;
    },
    setStep(state, action: PayloadAction<CreateSixStep>) {
      const { payload } = action;
      state.step = payload;
    },
    setScreenError(state, action: PayloadAction<string | undefined>) {
      const { payload } = action;
      state.screenError = payload;
    },
    resetCreateSixState() {
      return initialState;
    },
    setShowSuccessScreen(state, action: PayloadAction<boolean>) {
      const { payload } = action;
      state.showSuccessScreen = payload;
    },

    setDraftFromSix(state, action: PayloadAction<{ six: Six; participants?: ParticipantsResponseDTO }>) {
      const { six, participants } = action.payload;

      state.editingSixId = six.id;

      state.draft.name = six.name;
      state.draft.description = six.description;
      state.draft.ownerIds = six.owners.map((owner) => owner.id);

      state.draft.internalParticipantIds =
        participants?.internalParticipants.map((participant) => participant.id) ?? [];

      state.draft.externalParticipantIds =
        participants?.externalParticipants.map((participant) => participant.id) ?? [];

      state.draft.newExternalUsers = [];
      state.step = 'Name';
      state.showSuccessScreen = false;
      state.fieldErrors = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreateSixModalInformation.pending, (state) => {
        state.loadingInternal = true;
      })
      .addCase(
        fetchCreateSixModalInformation.fulfilled,
        (state, action: PayloadAction<CreateSixModalInformation>) => {
          state.loadingInternal = false;
          state.internalUsers = action.payload.internalUsers;
          state.externalUsers = action.payload.externalUsers;
        }
      )
      .addCase(fetchCreateSixModalInformation.rejected, (state, action) => {
        state.loadingInternal = false;
        const {
          error: { message },
        } = action;
        state.screenError = message;
      })
      // Validate name
      .addCase(validateSixName.pending, (state) => {
        state.submitting = true;
        state.fieldErrors = {};
        state.screenError = undefined;
      })
      .addCase(validateSixName.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(validateSixName.rejected, (state, action) => {
        state.submitting = false;

        const fieldErrors = action.payload?.fieldErrors;
        if (isFieldErrors(fieldErrors)) {
          state.fieldErrors = fieldErrors;
        } else {
          const {
            error: { message },
          } = action;
          state.screenError = message;
        }
      })
      // Create SIX
      .addCase(createSixThunk.pending, (state) => {
        state.submitting = true;
        state.fieldErrors = {};
        state.screenError = undefined;
      })
      .addCase(createSixThunk.fulfilled, (state, action: PayloadAction<SixSummary | null>) => {
        state.submitting = false;
        const { payload } = action;
        if (payload !== null) {
          state.created = { id: payload.id, name: payload.name };
        }
        resetCreateSixState();
      })
      .addCase(createSixThunk.rejected, (state, action) => {
        state.submitting = false;
        const fieldErrors = action.payload?.fieldErrors;
        if (isFieldErrors(fieldErrors)) {
          state.fieldErrors = fieldErrors;
        } else {
          const {
            error: { message },
          } = action;
          state.screenError = message;
        }
      });
  },
});

const { actions } = createSixSlice;

export const {
  setDraftField,
  setStep,
  setScreenError,
  resetCreateSixState,
  setShowSuccessScreen,
  setDraftFromSix,
} = actions;
export default createSixSlice.reducer;
